import type { Locale } from "../i18n/locale";
import type { AwsRegionId } from "./awsRegion";
import { DEFAULT_AUDIENCE_MODE, type AudienceMode } from "./audienceMode";
import {
  DEFAULT_COMPUTE_PROFILE,
  type ComputeProfileId,
} from "./computeProfile";
import { estimateMonthlyCost } from "./costEngine";
import {
  DEFAULT_DEPLOYMENT_STACK,
  type DeploymentStackConfig,
} from "./deploymentStack";
import { DEFAULT_LLM_PRICE_PROFILE, type LlmPriceProfile } from "./llmPriceProfile";
import { DEFAULT_SAVINGS_LEVERS, type SavingsLevers } from "./savingsLevers";
import type { Workload } from "./types";

export type SensitivityDeltas = {
  baseTotal: number;
  dauPlus10Usd: number;
  requestsPlus10Usd: number;
  aiSharePlus10Usd: number;
  tokensPlus10Usd: number;
};

export function computeSensitivity(
  w: Workload,
  locale: Locale,
  region: AwsRegionId,
  savings: SavingsLevers = DEFAULT_SAVINGS_LEVERS,
  stack: DeploymentStackConfig = DEFAULT_DEPLOYMENT_STACK,
  llmProfile: LlmPriceProfile = DEFAULT_LLM_PRICE_PROFILE,
  audience: AudienceMode = DEFAULT_AUDIENCE_MODE,
  computeProfile: ComputeProfileId = DEFAULT_COMPUTE_PROFILE,
): SensitivityDeltas {
  const base = estimateMonthlyCost(
    w,
    locale,
    region,
    savings,
    stack,
    llmProfile,
    audience,
    computeProfile,
  ).totalMonthlyUsd;
  const u10 = estimateMonthlyCost(
    { ...w, usersDaily: Math.max(1, Math.round(w.usersDaily * 1.1)) },
    locale,
    region,
    savings,
    stack,
    llmProfile,
    audience,
    computeProfile,
  ).totalMonthlyUsd;
  const r10 = estimateMonthlyCost(
    { ...w, requestsPerUser: Math.max(1, Math.round(w.requestsPerUser * 1.1)) },
    locale,
    region,
    savings,
    stack,
    llmProfile,
    audience,
    computeProfile,
  ).totalMonthlyUsd;
  const ai10 = estimateMonthlyCost(
    {
      ...w,
      aiRequestShare: Math.min(1, w.aiRequestShare * 1.1),
    },
    locale,
    region,
    savings,
    stack,
    llmProfile,
    audience,
    computeProfile,
  ).totalMonthlyUsd;
  const tok10 = estimateMonthlyCost(
    {
      ...w,
      avgPromptTokens: Math.max(1, Math.round(w.avgPromptTokens * 1.1)),
      avgCompletionTokens: Math.max(1, Math.round(w.avgCompletionTokens * 1.1)),
    },
    locale,
    region,
    savings,
    stack,
    llmProfile,
    audience,
    computeProfile,
  ).totalMonthlyUsd;
  return {
    baseTotal: base,
    dauPlus10Usd: u10 - base,
    requestsPlus10Usd: r10 - base,
    aiSharePlus10Usd: ai10 - base,
    tokensPlus10Usd: tok10 - base,
  };
}
